/**
 * Passkey-Verwaltung (Sicherheit-Tab im Profil)
 * Self-Service: Mitarbeiter:innen registrieren / verwalten / entfernen
 * ihre eigenen Passkeys (Touch ID / Windows Hello / Fingerabdruck / YubiKey).
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Fingerprint, Trash2, Loader2, ShieldCheck, AlertCircle, Plus, Pencil, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  isWebAuthnSupported, registerPasskey, listPasskeys, deletePasskey, renamePasskey,
} from '../../lib/webauthn';

const formatDate = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const PasskeySection = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const supported = isWebAuthnSupported();

  const reload = async () => {
    try {
      setLoading(true);
      const list = await listPasskeys();
      setCredentials(list);
    } catch (e) {
      toast.error('Konnte Passkey-Liste nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleOpenRegister = () => {
    const defaultName = (() => {
      const ua = navigator.userAgent;
      if (/iPhone/.test(ua)) return 'iPhone';
      if (/iPad/.test(ua)) return 'iPad';
      if (/Macintosh/.test(ua)) return 'Mac';
      if (/Android/.test(ua)) return 'Android-Gerät';
      if (/Windows/.test(ua)) return 'Windows-PC';
      return 'Mein Gerät';
    })();
    setDeviceName(defaultName);
    setRegisterOpen(true);
  };

  const handleRegister = async () => {
    const name = deviceName.trim() || 'Mein Gerät';
    setRegistering(true);
    try {
      await registerPasskey(name);
      toast.success(`Passkey „${name}" erfolgreich registriert`);
      setRegisterOpen(false);
      await reload();
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        toast.info('Registrierung abgebrochen');
        return;
      }
      const detail = err?.response?.data?.detail || err?.message || 'Registrierung fehlgeschlagen';
      toast.error(detail);
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (cred) => {
    if (!window.confirm(`Passkey „${cred.name}" wirklich entfernen? Du kannst dich danach nicht mehr per Fingerabdruck von diesem Gerät anmelden.`)) {
      return;
    }
    try {
      await deletePasskey(cred.id);
      toast.success('Passkey entfernt');
      await reload();
    } catch (e) {
      toast.error('Konnte Passkey nicht entfernen');
    }
  };

  const startRename = (cred) => {
    setRenameId(cred.id);
    setRenameValue(cred.name);
  };

  const confirmRename = async () => {
    const newName = renameValue.trim();
    if (!newName) {
      setRenameId(null);
      return;
    }
    try {
      await renamePasskey(renameId, newName);
      toast.success('Name aktualisiert');
      setRenameId(null);
      await reload();
    } catch (e) {
      toast.error('Konnte Name nicht ändern');
    }
  };

  return (
    <Card className="border-border/60" data-testid="passkey-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="h-5 w-5 text-primary" />
          Anmeldung per Fingerabdruck (Passkey)
        </CardTitle>
        <CardDescription>
          Melde dich künftig schneller und sicherer mit Touch ID, Windows Hello, Android-Fingerabdruck oder einem Sicherheits-Token an – ohne Passwort.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:border-amber-700 dark:text-amber-200">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Dieser Browser unterstützt keine Passkeys. Bitte nutze einen aktuellen Chrome, Edge, Safari oder Firefox.
            </div>
          </div>
        )}

        {supported && (
          <Button
            onClick={handleOpenRegister}
            className="gap-2"
            data-testid="passkey-register-btn"
          >
            <Plus className="h-4 w-4" />
            Neuen Passkey registrieren
          </Button>
        )}

        {/* List */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Lade Geräte …
            </div>
          ) : credentials.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Du hast noch keine Passkeys registriert.
            </p>
          ) : (
            credentials.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 p-3"
                data-testid={`passkey-row-${c.id}`}
              >
                <Fingerprint className="h-5 w-5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  {renameId === c.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className="h-8 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') confirmRename();
                          if (e.key === 'Escape') setRenameId(null);
                        }}
                      />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={confirmRename}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenameId(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Registriert: {formatDate(c.created_at)}
                        {c.last_used_at && ` · zuletzt verwendet: ${formatDate(c.last_used_at)}`}
                      </p>
                    </>
                  )}
                </div>
                {renameId !== c.id && (
                  <>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => startRename(c)}
                      title="Umbenennen"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(c)}
                      data-testid={`passkey-delete-${c.id}`}
                      title="Entfernen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* Register Dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="sm:max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Neuen Passkey registrieren</DialogTitle>
            <DialogDescription>
              Gib deinem Gerät einen Namen, damit du es später wiedererkennen kannst. Nach „Registrieren" fordert dein Browser dich zur biometrischen Bestätigung auf.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="device-name">Gerätename</Label>
            <Input
              id="device-name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="z.B. MacBook Büro, iPhone privat …"
              data-testid="passkey-name-input"
              maxLength={60}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterOpen(false)} disabled={registering}>
              Abbrechen
            </Button>
            <Button onClick={handleRegister} disabled={registering} data-testid="passkey-confirm-register-btn">
              {registering ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Wird registriert …</>
              ) : (
                <><Fingerprint className="h-4 w-4 mr-2" /> Registrieren</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PasskeySection;
